import { useRef, type ReactNode, type RefObject } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import './Dialog.css';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** One sentence under the title: what deciding here does. */
  description?: string;
  children: ReactNode;
  /** Buttons, right-aligned; the primary action last. */
  actions?: ReactNode;
  /** 'drawer' slides from the right edge for utilities such as settings. */
  variant?: 'modal' | 'drawer';
  size?: 'sm' | 'md' | 'lg';
  /**
   * Where the keyboard goes when the dialog closes, when what opened it is no longer on the page (a
   * menu row that closed with its menu). Without it the focus returns to what had it on opening.
   */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * What had the keyboard when the dialog opened gets it back when it closes: a dialog opened by
 * state has no trigger of its own for the library to return to, and would leave the focus nowhere.
 */
function useFocusReturn(returnFocusTo: RefObject<HTMLElement | null> | undefined) {
  const opener = useRef<HTMLElement | null>(null);
  return {
    remember: () => {
      opener.current = document.activeElement as HTMLElement | null;
    },
    giveBack: (event: Event) => {
      const back = returnFocusTo?.current ?? opener.current;
      if (!back?.isConnected) return;
      event.preventDefault();
      back.focus();
    },
  };
}

/**
 * Radix Dialog with the Paige chrome. Modal for decisions with evidence ("Go automatic");
 * drawer for settings, which are a utility and not a destination (interface principles).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  variant = 'modal',
  size = 'md',
  returnFocusTo,
}: DialogProps): ReactNode {
  const focus = useFocusReturn(returnFocusTo);
  const classes = ['pui-dialog', `pui-dialog--${variant}`, `pui-dialog--${size}`].join(' ');
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="pui-dialog__overlay" />
        <RadixDialog.Content
          className={classes}
          onOpenAutoFocus={focus.remember}
          onCloseAutoFocus={focus.giveBack}
        >
          <header className="pui-dialog__header">
            <RadixDialog.Title className="pui-dialog__title">{title}</RadixDialog.Title>
            <RadixDialog.Close className="pui-dialog__close" aria-label="Close">
              {'×'}
            </RadixDialog.Close>
          </header>
          {description ? (
            <RadixDialog.Description className="pui-dialog__desc">
              {description}
            </RadixDialog.Description>
          ) : null}
          <div className="pui-dialog__body">{children}</div>
          {actions ? <footer className="pui-dialog__actions">{actions}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
